'use client'

import { FaqQuestion } from '@/lib/apis/sanity'
import { PortableText, PortableTextReactComponents } from '@portabletext/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import React, { useState } from 'react'
import clsx from 'clsx'
import { motion } from 'framer-motion'
import { blocksToText } from '@/lib/utils'

type Props = {
  id: string
  questions: Array<FaqQuestion>
}

const Components: Partial<PortableTextReactComponents> = {
  list: {
    bullet: ({ children }) => (
      <ul className="my-2.5 max-w-3xl list-disc space-y-2 sm:space-y-4">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="my-2.5 list-decimal space-y-2 sm:space-y-4">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="ml-4 sm:ml-8">{children}</li>,
    number: ({ children }) => <li className="ml-4 sm:ml-8">{children}</li>,
  },

  marks: {
    link: ({ value, children }) => {
      return (
        <a
          className="cursor-pointer font-medium text-blue-600 transition-colors hover:text-blue-500"
          href={value?.href}
          target="_blank"
          rel={'noindex nofollow'}
        >
          {children}
        </a>
      )
    },
  },

  hardBreak: false,
}

export default function FaqQuestionList({ id, questions }: Props) {
  return (
    <div className="divide-y-gray-800 flex flex-col gap-y-4 divide-y">
      {questions.map((question, idx) => (
        <Question key={`faq-${id}-${idx}`} question={question} index={idx} />
      ))}
    </div>
  )
}

type QuestionProps = {
  question: FaqQuestion
  index: number
}

function Question({ question, index }: QuestionProps) {
  const [collapsed, setCollapsed] = useState(false)
  return (
    <div className={clsx('flex flex-col gap-y-2', index > 0 && 'pt-3')}>
      <div
        className="z-[2] flex cursor-pointer items-start justify-between"
        onClick={() => setCollapsed(!collapsed)}
      >
        <h3 className="font-sans text-xl text-gray-800">{question.question}</h3>
        <div className="mt-1">
          <motion.div
            animate={{ rotate: collapsed ? 45 : 0 }}
            transition={{ ease: 'easeOut', duration: 0.2 }}
            aria-hidden={true}
          >
            <FontAwesomeIcon icon={faPlus} size={'lg'} />
          </motion.div>
        </div>
      </div>

      <motion.div
        className={clsx(
          'z-[1] flex flex-col gap-y-1 text-justify font-sans leading-6 text-slate-800',
        )}
        initial="closed"
        animate={collapsed ? 'opened' : 'closed'}
        variants={{
          opened: { opacity: 1, height: 'auto' },
          closed: { opacity: 0, height: 0 },
        }}
        transition={{ ease: 'easeOut', duration: 0.2 }}
      >
        <PortableText value={question.answer} components={Components} />
      </motion.div>
    </div>
  )
}
